import { gridPrint } from './gridprint.js';

/* ===============================
   Job Card Print Customization
   =============================== */

const _origSetPrintLayout = gridPrint.setPrintLayout;
const _origResetPrintLayout = gridPrint.resetPrintLayout;

function _getJobCardContextEl() {
  // This element exists only on the Job Card *dataform* view (sidepanel container).
  // Landing/list grids should NOT trigger Job Card-specific print.
  return (
    document.querySelector('[data-formname="jobcard"][data-jobname][data-jobid]') ||
    document.querySelector('[data-jobname][data-jobid][data-jobcardno]')
  );
}

/* ===============================
   Override Print Layout
   =============================== */
gridPrint.setPrintLayout = function (elOrId) {
  const el = gridPrint._resolveGridElement(elOrId);
  if (!el) return;

  const title = (el.dataset.printTitle || '').toLowerCase();
  const formName = (el.dataset.formname || '').toLowerCase();

  const isJobCard =
    title.includes('job card') || formName === 'jobcard';

  // Call original print logic first
  _origSetPrintLayout.call(gridPrint, el);

  // Prevent duplicate injection
  if (!isJobCard || el.querySelector('.jobcard-print-label')) return;

  // Only enable Job Card-specific header/footer when we're inside the Job Card *dataform* view.
  // (Landing/list view should use normal grid print without extra Job Card meta.)
  const ctxEl = _getJobCardContextEl();
  if (!ctxEl) return;

  const jc = getJobCardData();

  /* ---------- Header Label ---------- */
  const label = document.createElement('div');
  label.className = 'jobcard-print-label';
  label.style.width = "100%";
  label.style.display = "block";
  label.style.textAlign = "center";
  label.style.margin = "0";
  label.style.padding = "0";
  label.style.boxSizing = "border-box";
  label.innerHTML = `
  <div class="jobcard-meta-wrap">
    <div style="font-size:16px;font-weight:600;">
      Job Name: ${jc.jobname || '-'} &nbsp; | &nbsp;
      Job ID: ${jc.jobid || '-'} &nbsp; | &nbsp;
      Job Card No: ${jc.jobcardno || '-'}${jc.jobcarddate ? ` (${(() => { const [yyyy, mm, dd] = jc.jobcarddate.split('-'); return `${dd}-${mm}-${yyyy}`; })()})` : ''}
    </div>
    <div style="margin-top:4px; font-size:16px; font-weight:600;">
      Jobcard Type: ${['true', '1', true].includes(jc.builtup) ? 'Built-up' : 'Others'} &nbsp; | &nbsp;
      Customer Name: ${jc.customer_name || '-'} &nbsp; | &nbsp;
      Structure: ${jc.structureDisplay || '-'}
    </div>
    ${jc.specialRemarks ? `
      <div style="margin-top:4px; font-size:16px; font-weight:600;">
        Special Remarks: ${jc.specialRemarks}
      </div>
    ` : ''}
  </div>
`;

  /* ---------- Footer ---------- */
  const footer = document.createElement('div');
  footer.className = 'jobcard-print-footer';
  footer.style.marginTop = '20px';
  footer.style.paddingTop = '10px';
  footer.style.borderTop = '1px solid #000';
  footer.innerHTML = `
    <p style="margin:0;"><b>Jobcard Remarks:</b> ${jc.remarks || ' '}</p>
    <div style="display:flex;justify-content:space-between;width:100%;">
      <p style="margin:0;"><b>Jobcard Issued By</b> ${jc.issued_by || ' '}</p>
      <p style="margin:0;"><b>Line Incharge</b> ${jc.lineIncharge || ' '}</p>
      <p style="margin:0;"><b>Manager Production</b> ${jc.managerName || ' '}</p>
    </div>
  `;

  /* ---------- Insert into Print DOM ---------- */
  requestAnimationFrame(() => {
      const root = el; // outermost print element

      // Find AG titlebar
      const titleBar =
        root.querySelector('.ag-print-titlebar') ||
        root.querySelector('.ag-print-title')?.closest?.('.ag-print-titlebar');

      // ✅ Insert label right after titlebar BUT OUTSIDE AG GRID wrapper padding effect
      if (titleBar && titleBar.parentNode) {
        titleBar.parentNode.insertBefore(label, titleBar.nextSibling);
      } else {
        root.prepend(label);
      }

        // Always append footer at the bottom
      root.appendChild(footer);
  });
}


/* ===============================
   Fetch Job Card Data
   =============================== */
function getJobCardData() {
  const el = _getJobCardContextEl();

  if (!el) {
    console.warn('JobCard data element not found');
    return {};
  }

  return {
    jobname: el.dataset.jobname || '',
    jobid: el.dataset.jobid || '',
    jobcardno: el.dataset.jobcardno || '',
    jobcarddate: el.dataset.jobcarddate || '',
    builtup: el.dataset.builtup || 'false',
    specialRemarks: el.dataset.specialRemarks || '',
    customer_name: el.dataset.customerName || '',
    structureDisplay: el.dataset.structureDisplay || '',
    issued_by: el.dataset.issuedBy || '',
    lineIncharge: el.dataset.lineIncharge || '',
    remarks: el.dataset.remarks || '',
    managerName: el.dataset.managerName || ''
  };
}

/* ===============================
   Cleanup on Exit
   =============================== */
gridPrint.resetPrintLayout = function (elOrId) {
  const el = gridPrint._resolveGridElement(elOrId);
  if (!el) return;

  el.querySelector('.jobcard-print-label')?.remove();
  el.querySelector('.jobcard-print-footer')?.remove();

  _origResetPrintLayout.call(gridPrint, el);
};

/* ===============================
   Print CSS
   =============================== */
(function () {
  if (document.getElementById('jobcard-print-style')) return;

  const style = document.createElement('style');
  style.id = 'jobcard-print-style';
  style.textContent = `
    /* Keep header text centered even in print preview */
    .jobcard-print-label {
      text-align: center;
      width: 100%;
      margin: 4px 0 8px 0;
      padding: 0;
      box-sizing: border-box;
    }

    /* ✅ This is the important part to truly center the details text block */
    .jobcard-print-label .jobcard-meta-wrap {
      display: inline-block;
      text-align: center;
      max-width: 100%;
    }

    @media print {
      .jobcard-print-label {
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        margin: 4px 0 8px 0;
        border-bottom: 1px solid #000;
        padding-bottom: 4px;
      }

      .jobcard-print-label{
      width:100% !important;
      text-align:center !important;
      margin: 4px 0 8px 0 !important;
      padding:0 !important;
      }

      .jobcard-print-footer {
        font-size: 16px;
      }
      .ag-print-titlebar{
        width: 100%;
      }
    }
  `;
  document.head.appendChild(style);
})();

// Removing Test comment for checking mergeback working

